import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { ComplianceAnalysisResult } from '../../shared/types';
import { buildAnalysisPdfHtml, type PdfReportMeta } from './pdfHtml';
import { C } from './reportTheme';

/**
 * Génère et télécharge un PDF du rapport d'analyse à la charte Visaly
 * (« Cream & Ink » : pages crème, cartes pastel, encre).
 *
 * Le HTML (buildAnalysisPdfHtml) est rendu dans une **iframe isolée** : son
 * document n'embarque aucune feuille de style Tailwind, donc aucune couleur
 * `oklch` que html2canvas 1.x ne sait pas parser (c'était la cause des échecs
 * qui faisaient retomber sur la boîte d'impression — laquelle supprime les
 * fonds de couleur). On capture ensuite une image raster : les cartes
 * colorées sont donc préservées, y compris à l'impression.
 *
 * Le crème du fond n'est PAS peint par le HTML mais par jsPDF, page par
 * page : une page qui se termine à mi-hauteur reste crème jusqu'en bas, et
 * les cartes flottent dessus comme les tuiles de l'application.
 *
 * Fallback ultime : si la capture échoue malgré tout, on ouvre la boîte
 * d'impression du navigateur sur le même HTML (print-color-adjust:exact).
 */

/** Crème de la charte, en composantes jsPDF. */
const CREAM_RGB: [number, number, number] = [239, 231, 218];

const MARGIN_X = 46;   // colonne de composition, en points
const MARGIN_TOP = 46;
const MARGIN_BOTTOM = 62;  // laisse respirer le pied de page paginé
const GAP = 14;        // écart vertical entre deux cartes

export async function downloadAnalysisPdf(
  result: ComplianceAnalysisResult,
  filename = 'analyse-visaly',
  meta: PdfReportMeta = {},
): Promise<void> {
  const generatedAt = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const html = buildAnalysisPdfHtml(result, generatedAt, meta);

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '720px',
    height: '2200px',
    border: '0',
    background: C.cream,
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe document indisponible');
    doc.open();
    doc.write(html);
    doc.close();

    // Attendre le chargement des polices (Outfit, Acorn) + un tick de mise en
    // page. Les @font-face sont en `font-display:block` : sans cette attente,
    // la capture fige un rendu en police système, qu'on ne rattrape pas.
    try { await doc.fonts?.ready; } catch { /* API Font Loading absente */ }
    await new Promise((r) => setTimeout(r, 150));

    const blocks = Array.from(doc.querySelectorAll<HTMLElement>('.pdf-block'));
    if (blocks.length === 0) throw new Error('aucun bloc à exporter');

    const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const contentW = pageW - MARGIN_X * 2;
    const usableH = pageH - MARGIN_TOP - MARGIN_BOTTOM;

    /** Peint le crème sur toute la page courante. À appeler AVANT de poser
     *  quoi que ce soit dessus — jsPDF n'a pas de calque d'arrière-plan. */
    const paintPage = () => {
      pdf.setFillColor(...CREAM_RGB);
      pdf.rect(0, 0, pageW, pageH, 'F');
    };
    const nextPage = () => { pdf.addPage(); paintPage(); };

    paintPage();
    let y = MARGIN_TOP;

    for (const block of blocks) {
      const canvas = await html2canvas(block, {
        scale: 2,
        backgroundColor: C.cream,   // les angles arrondis se fondent dans la page
        logging: false,
        useCORS: true,
      });
      const imgH = (canvas.height * contentW) / canvas.width;

      if (imgH <= usableH) {
        // Bloc entier — on saute à la page suivante s'il ne tient pas ici.
        if (y + imgH > pageH - MARGIN_BOTTOM) { nextPage(); y = MARGIN_TOP; }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', MARGIN_X, y, contentW, imgH);
        y += imgH + GAP;
      } else {
        // Bloc plus haut qu'une page : on le découpe sur plusieurs pages.
        if (y > MARGIN_TOP) { nextPage(); y = MARGIN_TOP; }
        let srcY = 0;
        let remaining = imgH;
        while (remaining > 0.5) {
          const avail = pageH - MARGIN_BOTTOM - y;
          const sliceH = Math.min(avail, remaining);
          const srcSlicePx = Math.round((sliceH * canvas.width) / contentW);
          const srcYpx = Math.round((srcY * canvas.width) / contentW);
          const tmp = document.createElement('canvas');
          tmp.width = canvas.width;
          tmp.height = srcSlicePx;
          const ctx = tmp.getContext('2d');
          if (ctx) {
            ctx.fillStyle = C.cream;
            ctx.fillRect(0, 0, tmp.width, tmp.height);
            ctx.drawImage(canvas, 0, srcYpx, canvas.width, srcSlicePx, 0, 0, canvas.width, srcSlicePx);
          }
          pdf.addImage(tmp.toDataURL('image/jpeg', 0.95), 'JPEG', MARGIN_X, y, contentW, sliceH);
          remaining -= sliceH;
          srcY += sliceH;
          y += sliceH;
          if (remaining > 0.5) { nextPage(); y = MARGIN_TOP; }
        }
        y += GAP;
      }
    }

    stampFooters(pdf, pageW, pageH);
    pdf.save(`${filename}.pdf`);
  } catch (err) {
    console.error('[pdf] capture failed, falling back to print dialog', err);
    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    }
  } finally {
    document.body.removeChild(iframe);
  }
}

/** Pied de page paginé, posé en vectoriel sur chaque page une fois le
 *  document composé — c'est seulement là qu'on connaît le nombre de pages. */
function stampFooters(pdf: jsPDF, pageW: number, pageH: number): void {
  const total = pdf.getNumberOfPages();
  const baseline = pageH - 30;
  for (let p = 1; p <= total; p++) {
    pdf.setPage(p);
    // Filet crème foncé plutôt qu'un trait gris : il doit disparaître dans
    // le fond, pas souligner le texte.
    pdf.setDrawColor(215, 204, 188);
    pdf.setLineWidth(0.5);
    pdf.line(MARGIN_X, baseline - 12, pageW - MARGIN_X, baseline - 12);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(127, 122, 110);
    pdf.text('Visaly · Analyse publicité santé · visaly.fr', MARGIN_X, baseline);
    pdf.text(`${p} / ${total}`, pageW - MARGIN_X, baseline, { align: 'right' });
  }
}

/** Slugifie un titre pour en faire un nom de fichier sûr. */
export function pdfFilenameFor(title: string | undefined, fallback = 'analyse-visaly'): string {
  const base = (title ?? '').trim();
  if (!base) return fallback;
  const slug = base
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return slug ? `visaly-${slug}` : fallback;
}
